import React, { useState, useEffect, useRef } from "react";

export default function Search({ isVisible, onClose, onSearch, placeholder = "Search...", initialText = "", onTextChange, position = "center", onEnter, onEscape }) {
  const [searchText, setSearchText] = useState(initialText);
  const inputRef = useRef();
  const debounceRef = useRef();
  const hasUserTypedRef = useRef(false);

  useEffect(() => {
    if (isVisible && inputRef.current) {
      // Use setTimeout to ensure the input is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Only select all text if input is empty, otherwise place cursor at end
          // Use initialText to check, as searchText might not be updated yet
          if (!initialText) {
            inputRef.current.select();
          } else {
            // Place cursor at end of text
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        }
      }, 0);
      hasUserTypedRef.current = false;
    } else if (!isVisible && inputRef.current) {
      // Blur the input when Search closes to ensure it doesn't intercept keyboard events
      inputRef.current.blur();
      hasUserTypedRef.current = false;
    }
  }, [isVisible, initialText]);

  useEffect(() => {
    if (initialText !== searchText && !hasUserTypedRef.current) {
      setSearchText(initialText);
    }
  }, [initialText, searchText]);

  useEffect(() => {
    if (onSearch && isVisible) {
      // Clear existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      
      // Debounce the search call
      debounceRef.current = setTimeout(() => {
        onSearch(searchText);
      }, 100);
    }
  }, [searchText, onSearch, isVisible]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setSearchText("");
      if (onEscape) {
        onEscape();
      }
      onClose();
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (onEnter) {
        onEnter();
      }
      setSearchText("");
      onClose();
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    hasUserTypedRef.current = true;
    if (onTextChange) {
      onTextChange(newValue);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100001,
        display: "flex",
        alignItems: position === "top" ? "flex-start" : "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.1)",
        paddingTop: position === "top" ? "150px" : "0",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: "20px 30px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
          minWidth: 400,
          maxWidth: 600,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            width: "100%",
            border: "none",
            outline: "none",
            fontSize: 18,
            fontWeight: 500,
            color: "#333",
            background: "transparent",
          }}
        />
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#666",
            textAlign: "center",
          }}
        >
          Press Escape to close
        </div>
      </div>
    </div>
  );
}
